import { z } from 'zod'

// ─── Enums (mirror van DB-enums) ─────────────────────────────────────────────
//
// Spec-uitbreiding 2026-05: drie nieuwe route-soorten en acht spec-categorieën
// (§5.3). §10.2 (architectuurbeslissingen 2026-05-18) heeft de drie legacy-
// waarden TIJD/KM/BEIDE definitief verwijderd (zie migration
// 20260518150500_reisafwijking_type_cleanup). Bestaande rijen met legacy-
// waarden moeten al opgeruimd zijn vóór de migratie draait.

export const ROUTE_TYPES = ['HEEN', 'TERUG', 'WOON_WERK', 'WERK_WERK', 'WERK_WOON'] as const
export type RouteType = (typeof ROUTE_TYPES)[number]

export const ROUTE_TYPE_LABELS: Record<RouteType, string> = {
  HEEN:      'Heen',
  TERUG:     'Terug',
  WOON_WERK: 'Woon → Werk',
  WERK_WERK: 'Werk → Werk',
  WERK_WOON: 'Werk → Woon',
}

/** NL-label voor een reis-route-type. WOON_WERK/WERK_WOON renderen als Heen/Terug
 *  zodat de monteur-UX identiek blijft bij het enkel-werkbon-dag (de norm);
 *  WERK_WERK = tussenrit bij meerdere stops op één dag. Legacy HEEN/TERUG behouden. */
export function routeTypeLabel(rt: RouteType | string): string {
  switch (rt) {
    case 'WOON_WERK':
    case 'HEEN':
      return 'Heen'
    case 'WERK_WOON':
    case 'TERUG':
      return 'Terug'
    case 'WERK_WERK':
      return 'Naar volgende locatie'
    default:
      return String(rt)
  }
}

export const REIS_AFWIJKING_TYPES = [
  // Spec §5.3 — 8 categorieën (legacy TIJD/KM/BEIDE verwijderd per §10.2).
  'FILE_VERTRAGING',
  'OMLEIDING',
  'TUSSENSTOP_MATERIAAL',
  'TUSSENSTOP_OVERIG',
  'FOUT_BEREKENING',
  'PECH_ONDERWEG',
  'WEER_OMSTANDIGHEDEN',
  'ANDERS',
  'NIET_GEREDEN',
] as const
export type ReisAfwijkingType = (typeof REIS_AFWIJKING_TYPES)[number]

export const REIS_AFWIJKING_TYPE_LABELS: Record<ReisAfwijkingType, string> = {
  FILE_VERTRAGING:      'File / vertraging',
  OMLEIDING:            'Omleiding',
  TUSSENSTOP_MATERIAAL: 'Tussenstop materiaal',
  TUSSENSTOP_OVERIG:    'Tussenstop overig',
  FOUT_BEREKENING:      'Berekening klopt niet',
  PECH_ONDERWEG:        'Pech onderweg',
  WEER_OMSTANDIGHEDEN:  'Weersomstandigheden',
  ANDERS:               'Anders',
  NIET_GEREDEN:         'Niet gereden',
}

export const REIS_AFWIJKING_STATUSES = [
  'INGEDIEND',
  'GOEDGEKEURD',
  'AFGEWEZEN',
  'NIET_VAN_TOEPASSING',
] as const
export type ReisAfwijkingStatus = (typeof REIS_AFWIJKING_STATUSES)[number]

/**
 * `LUCHTLIJN` (batch-50, FO-403) onderscheidt de Haversine-schatting van een
 * handmatig vastgezette standaard. Beide schreven vroeger `HANDMATIG` weg,
 * waardoor een schatting niet van een bewuste override te onderscheiden was.
 * `routeProvider` is een String-kolom (geen Prisma-enum), dus dit vraagt geen
 * migratie; bestaande `HANDMATIG`-rijen blijven geldig en worden bij het tonen
 * alsnog uit elkaar gehaald met een cache-lookup (zie `ReisHerkomstDTO`).
 */
export const REIS_ROUTE_PROVIDERS = [
  'GOOGLE_MAPS',
  'GOOGLE_ROUTES',
  'OSRM',
  'HANDMATIG',
  'LUCHTLIJN',
] as const
export type ReisRouteProvider = (typeof REIS_ROUTE_PROVIDERS)[number]

// ─── Herkomst van een reistijd (batch-50 reistijd-geocode, FO-403 §3.D) ──────

export const REIS_HERKOMST_BRONNEN = [
  'GOOGLE_ROUTE',
  'HANDMATIGE_STANDAARD',
  'LUCHTLIJN_SCHATTING',
] as const
export type ReisHerkomstBron = (typeof REIS_HERKOMST_BRONNEN)[number]

export const REIS_HERKOMST_LABELS: Record<ReisHerkomstBron, string> = {
  GOOGLE_ROUTE:         'Google-route',
  HANDMATIGE_STANDAARD: 'Handmatige standaard',
  LUCHTLIJN_SCHATTING:  'Luchtlijn-schatting',
}

/** Korte uitleg onder het herkomst-label; zonder jargon (spec §12.6). */
export const REIS_HERKOMST_UITLEG: Record<ReisHerkomstBron, string> = {
  GOOGLE_ROUTE:         'Berekend via de route over de weg.',
  HANDMATIGE_STANDAARD: 'Vastgezette standaard voor dit adres-paar.',
  LUCHTLIJN_SCHATTING:  'Schatting (adres niet gevonden) - hemelsbreed omgerekend.',
}

/**
 * Herkomst afgeleid uit de opgeslagen provider. `HANDMATIG` is dubbelzinnig voor
 * rijen van vóór FO-403: de server verfijnt die met een cache-lookup (bestaat er
 * een HANDMATIG-cacherij voor dit paar, dan is het een vastgezette standaard).
 */
export function reisHerkomstVanProvider(provider: ReisRouteProvider | string): ReisHerkomstBron {
  switch (provider) {
    case 'LUCHTLIJN':
      return 'LUCHTLIJN_SCHATTING'
    case 'HANDMATIG':
      return 'HANDMATIGE_STANDAARD'
    default:
      return 'GOOGLE_ROUTE'
  }
}

/** Waarom een reistijd-snapshot om herberekening vraagt (spec §3.D7/D8). */
export const REIS_HERREKEN_REDENEN = ['AFWIJKING', 'CACHE_WEG', 'CACHE_AFGEKEURD'] as const
export type ReisHerrekenReden = (typeof REIS_HERREKEN_REDENEN)[number]

export interface ReisHerrekenSignaal {
  reden: ReisHerrekenReden
  /** Kant-en-klare zin voor de UI. */
  melding: string
}

/** De huidige standaard uit de route-cache voor het adres-paar van deze leg. */
export interface ReisHuidigeStandaard {
  kilometers: number
  minuten: number
  /** `DistanceCacheSource` van de cache-rij ('MAPBOX' = extern berekend). */
  bron: string
  fetchedAt: string
}

export interface ReisHerkomstDTO {
  berekeningId: string
  bron: ReisHerkomstBron
  /** Ruwe provider-waarde zoals opgeslagen (voor debug/audit). */
  provider: ReisRouteProvider
  /** Wanneer deze reistijd berekend/bijgewerkt is (ISO). */
  berekendOp: string
  /** Reistijd in het snapshot, in minuten (incl. structurele correctie). */
  snapshotMinuten: number
  huidigeStandaard: ReisHuidigeStandaard | null
  /** Relatieve afwijking snapshot vs huidige standaard (0.12 = 12%). */
  afwijkingFractie: number | null
  herrekenSignaal: ReisHerrekenSignaal | null
}

/**
 * Drempel waarboven een snapshot als "verouderd" geldt t.o.v. de huidige
 * standaard (aanname A2). Bewust één constante zodat API en UI niet uiteenlopen.
 */
export const REIS_SNAPSHOT_AFWIJKING_DREMPEL = 0.1

// ─── Structurele reis-correctie (batch-3 stream C) ───────────────────────────
//
// Richtingen waarop een structurele correctie ingesteld kan worden. Legacy
// HEEN/TERUG zijn bewust uitgesloten — nieuwe correcties gebruiken de moderne
// WOON_WERK / WERK_WERK / WERK_WOON-tripel. `null` (alle richtingen) is zeldzaam
// en wordt op het schema apart als nullable toegestaan.
export const REIS_CORRECTIE_RICHTINGEN = ['WOON_WERK', 'WERK_WERK', 'WERK_WOON'] as const
export type ReisCorrectieRichting = (typeof REIS_CORRECTIE_RICHTINGEN)[number]

/**
 * Normaliseer een (mogelijk legacy) routeType naar een correctie-richting:
 * legacy HEEN -> WOON_WERK, TERUG -> WERK_WOON; moderne waarden blijven; onbekend
 * (of null) -> null = "niet structureel vastzetbaar". Eén bron van waarheid voor
 * zowel de engine (`maakStructureleCorrectieVanAfwijking`) als de
 * goedkeur-UI (de "vastzetten"-keuze toont/disabled op deze waarde).
 */
export function normaliseerReisCorrectieRichting(
  routeType: RouteType | string | null,
): ReisCorrectieRichting | null {
  switch (routeType) {
    case 'WOON_WERK':
    case 'HEEN':
      return 'WOON_WERK'
    case 'WERK_WOON':
    case 'TERUG':
      return 'WERK_WOON'
    case 'WERK_WERK':
      return 'WERK_WERK'
    default:
      return null
  }
}

/**
 * Transparantie-marker op een `ReisUrenBerekening`: legt vast dat de berekende
 * km/reistijd een structurele correctie bevat (opgeslagen in `routeData`, over de
 * DTO ontsloten). Voedt de "incl. structurele correctie (+15 min terug)"-weergave
 * op het reisuren-/werkbon-overzicht. Niets wordt dubbel geteld: de correctie zit
 * in `berekende*`, een handmatige afwijking blijft het verschil t.o.v. die
 * (al-gecorrigeerde) berekening.
 */
export interface StructureleCorrectieInfo {
  correctieId: string
  kmCorrectie: number
  reistijdMinutenCorrectie: number
}

/** DTO voor een structurele reis-correctie (beheer-UI onder /reisafstanden). */
export interface MedewerkerReisCorrectieDTO {
  id: string
  medewerkerId: string
  medewerkerNaam: string | null
  routeType: RouteType | null
  clientId: string | null
  clientNaam: string | null
  kmCorrectie: number
  reistijdMinutenCorrectie: number
  reden: string | null
  ingangsdatum: string // ISO yyyy-mm-dd
  einddatum: string | null // ISO yyyy-mm-dd
  actief: boolean
  aangemaaktDoorId: string
  aangemaaktDoorNaam: string | null
  createdAt: string
  updatedAt: string
}

// ─── DTO ─────────────────────────────────────────────────────────────────────

export interface ReisUrenBerekeningDTO {
  id: string
  medewerkerId: string
  werkbonId: string
  urenRegelId: string | null
  datum: string // ISO yyyy-mm-dd
  routeType: RouteType

  vertrekAdres: string
  aankomstAdres: string
  vertrekLat: number | null
  vertrekLng: number | null
  aankomstLat: number | null
  aankomstLng: number | null

  berekendeReisKm: number
  berekendeReistijdUren: number
  werkelijkeReistijdUren: number | null
  werkelijkeReisKm: number | null

  vergoedbareReistijdUren: number
  facturabeleReistijdUren: number
  uitbetaaldeReistijdUren: number

  // Gesnapshot €-reisvergoeding (zie schema). Over de wire al naar number
  // geconverteerd door toDTO (Decimal → number).
  facturabeleReisVergoeding: number
  uitbetaaldeReisVergoeding: number

  afwijkingType: ReisAfwijkingType | null
  afwijkingStatus: ReisAfwijkingStatus | null
  afwijkingReden: string | null
  afwijkingOpmerking: string | null
  afwijkingIngediendDoorId: string | null
  afwijkingBeoordeeldDoorId: string | null
  afwijkingBeoordelaarOpmerking: string | null
  afwijkingIngediendOp: string | null
  afwijkingBeoordeeldOp: string | null

  routeProvider: ReisRouteProvider

  // Transparantie (batch-50, FO-403): wanneer deze reistijd berekend/bijgewerkt
  // is. Voedt de "berekend op"-regel in de reistijd-detailweergave.
  berekendOp: string

  // Transparantie: gezet wanneer de berekende km/reistijd een structurele
  // correctie bevat (batch-3 stream C). null = geen structurele correctie.
  structureleCorrectie: StructureleCorrectieInfo | null

  createdAt: string
  updatedAt: string
}

// ─── Zod schemas ─────────────────────────────────────────────────────────────

export const berekenReisurenSchema = z.object({
  medewerkerId: z.string().min(1),
  werkbonId: z.string().min(1),
  // ISO date (yyyy-mm-dd) of full ISO datetime — service zet om naar date-only.
  datum: z.string().min(8),
})
export type BerekenReisurenInput = z.infer<typeof berekenReisurenSchema>

export const batchBerekenReisurenSchema = z.object({
  vanafDatum: z.string().min(8).optional(),
  totDatum: z.string().min(8).optional(),
  medewerkerId: z.string().min(1).optional(),
  werkbonId: z.string().min(1).optional(),
  forceRefresh: z.boolean().optional().default(false),
})
export type BatchBerekenReisurenInput = z.infer<typeof batchBerekenReisurenSchema>

export const dienAfwijkingInSchema = z.object({
  werkelijkeReistijdUren: z.number().nonnegative().max(24).optional(),
  werkelijkeReisKm: z.number().nonnegative().max(2000).optional(),
  afwijkingType: z.enum(REIS_AFWIJKING_TYPES),
  reden: z.string().min(3).max(120),
  // Toelichting is optioneel (2026-06): de dropdown-reden (afwijkingType) is de
  // verplichte keuze; een vrije-tekst toelichting mag, maar hoeft niet.
  opmerking: z.string().max(500).optional(),
})
export type DienAfwijkingInInput = z.infer<typeof dienAfwijkingInSchema>

export const goedkeurAfwijkingSchema = z.object({
  // Facturabel-keuze bij accepteren (werkbon-afwijking-facturabel-keuze):
  //   true  = afwijking ook naar de klant (factureren + op de werkbon noteren);
  //   false = alleen verlonen, de klant ziet de standaard/berekende waarde.
  // Default false: een afwijking wordt niet ongevraagd doorbelast aan de klant.
  // Accepteren raakt sowieso altijd het uitbetaalde (verloning) deel.
  facturabel: z.boolean().optional().default(false),
  opmerking: z.string().max(500).optional(),
})
export type GoedkeurAfwijkingInput = z.infer<typeof goedkeurAfwijkingSchema>

// Bij afwijzen is opmerking verplicht (Zod-refine, zoals brief vraagt).
export const wijsAfwijkingAfSchema = z
  .object({
    opmerking: z.string().max(500).optional(),
  })
  .refine((d) => typeof d.opmerking === 'string' && d.opmerking.trim().length >= 3, {
    message: 'Een afwijzing vereist een toelichting (min 3 tekens).',
    path: ['opmerking'],
  })
export type WijsAfwijkingAfInput = z.infer<typeof wijsAfwijkingAfSchema>

export const listAfwijkingenSchema = z.object({
  status: z.enum(REIS_AFWIJKING_STATUSES).optional(),
  medewerkerId: z.string().min(1).optional(),
  // payroll-werkruimte 18→8 — server-side filteren (de fetch is capped op 2000):
  // klant via de werkbon-relatie, reden-type op de afwijking zelf.
  klantId: z.string().min(1).optional(),
  afwijkingType: z.enum(REIS_AFWIJKING_TYPES).optional(),
  vanafDatum: z.string().min(8).optional(),
  totDatum: z.string().min(8).optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(2000).default(50),
})
export type ListAfwijkingenInput = z.infer<typeof listAfwijkingenSchema>

export const weekParamsSchema = z.object({
  medewerkerId: z.string().min(1),
  weekNr: z.coerce.number().int().min(1).max(53),
  jaar: z.coerce.number().int().min(2024).max(2100),
})
export type WeekParams = z.infer<typeof weekParamsSchema>

// ─── Structurele reis-correctie — schemas (gedeeld client + API) ─────────────
//
// Eén bron van waarheid: de API parset hiermee en het beheer-form valideert er
// client-side mee (CC.1). Nullable velden komen overeen met de read-DTO
// (routeType/clientId/einddatum/reden), zodat de PATCH zijn eigen GET niet weigert.

export const reisCorrectieRichtingSchema = z.enum(REIS_CORRECTIE_RICHTINGEN)

export const createReisCorrectieSchema = z.object({
  medewerkerId: z.string().min(1),
  // null = alle richtingen (zeldzaam); normaal WOON_WERK of WERK_WOON.
  routeType: reisCorrectieRichtingSchema.nullable().optional(),
  // null = woon-werk-traject van de medewerker; gezet = alleen voor deze klant.
  clientId: z.string().min(1).nullable().optional(),
  kmCorrectie: z.number().min(-1000).max(1000),
  reistijdMinutenCorrectie: z.number().int().min(-1440).max(1440),
  reden: z.string().max(500).nullable().optional(),
  // ISO yyyy-mm-dd (date-only) — de API normaliseert naar UTC-middernacht.
  ingangsdatum: z.string().min(8),
  einddatum: z.string().min(8).nullable().optional(),
  actief: z.boolean().optional(),
})
export type CreateReisCorrectieInput = z.infer<typeof createReisCorrectieSchema>

// Update: alle velden optioneel; medewerkerId ligt vast (verwijder + maak opnieuw
// aan voor een andere medewerker). Behoud van historie via actief=false / soft-delete.
export const updateReisCorrectieSchema = z.object({
  routeType: reisCorrectieRichtingSchema.nullable().optional(),
  clientId: z.string().min(1).nullable().optional(),
  kmCorrectie: z.number().min(-1000).max(1000).optional(),
  reistijdMinutenCorrectie: z.number().int().min(-1440).max(1440).optional(),
  reden: z.string().max(500).nullable().optional(),
  ingangsdatum: z.string().min(8).optional(),
  einddatum: z.string().min(8).nullable().optional(),
  actief: z.boolean().optional(),
})
export type UpdateReisCorrectieInput = z.infer<typeof updateReisCorrectieSchema>

// payroll-werkruimte 18→8 — sorteerbare kolommen op de server-gepagineerde
// correctielijst (nooit client-side over de pagina heen). Geen `sort` = de
// bestaande volgorde (actief eerst, dan ingangsdatum aflopend).
export const REIS_CORRECTIE_SORT = [
  'medewerker',
  'klant',
  'km',
  'minuten',
  'ingangsdatum',
  'status',
] as const
export type ReisCorrectieSort = (typeof REIS_CORRECTIE_SORT)[number]

export const listReisCorrectiesSchema = z.object({
  medewerkerId: z.string().min(1).optional(),
  // payroll-werkruimte 18→8 — klant-filter; richting matcht inclusief
  // richtingloze correcties (routeType null geldt voor beide richtingen).
  // Bewust alleen de twee woon-werk-richtingen (Heen/Terug) als filterwaarde.
  klantId: z.string().min(1).optional(),
  richting: z.enum(['WOON_WERK', 'WERK_WOON']).optional(),
  actief: z.enum(['true', 'false']).optional(),
  sort: z.enum(REIS_CORRECTIE_SORT).optional(),
  order: z.enum(['asc', 'desc']).default('asc'),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(200).default(100),
})
export type ListReisCorrectiesInput = z.infer<typeof listReisCorrectiesSchema>

// "Permanent overrulen" vanuit de afwijking-goedkeuring: maakt een
// MedewerkerReisCorrectie op basis van (werkelijk - berekend) en keurt de
// huidige afwijking meteen goed. `scopeToClient` = alleen voor de klant van deze
// werkbon (anders het woon-werk-traject, clientId null).
export const overruleReisAfwijkingSchema = z.object({
  scopeToClient: z.boolean().optional().default(false),
})
export type OverruleReisAfwijkingInput = z.infer<typeof overruleReisAfwijkingSchema>
